<script setup lang="ts">
import { onBeforeRouteUpdate, useRoute } from 'vue-router';
import { computed, watch, ref } from 'vue';
import { resolvePersonIdentifier } from '@/router/guards';
import type { ComputedRef } from 'vue';
import { usePersonStore } from '@/stores/person';
import PersonBanner from '@/components/person/PersonBanner.vue';
import PersonBasicDataErrorState from '@/components/person/PersonBasicDataErrorState.vue';
import Button from 'primevue/button';
import { formatTaxId } from '@/utils/formatters';
import TokenActionConfirmationDialog from '@/components/common/TokenActionConfirmationDialog.vue';
import { DATA_UNIT_PRODUCT_KEYS } from '@/constants/dataUnitProducts';
import { useDataUnitPrices } from '@/composables/useDataUnitPrices';
import { useAuthStore } from '@/stores/auth';
import { hasFeatureAccess, isCialInternalUser } from '@/utils/auth';
import { useEntityMonitoringStatus, useEntityEventCount } from '@/composables/useEntityMonitoring';
import { TaxIdStatusDescription } from '@/types/enums';
import BaseIcon from '@/components/base/BaseIcon.vue';

const route = useRoute();
const personStore = usePersonStore();
const authStore = useAuthStore();
const {
  loading: basicDataPriceLoading,
  error: basicDataPriceError,
  loadDataUnitPrices,
  getDataUnitPriceLabel,
} = useDataUnitPrices();

const uuid: ComputedRef<string> = computed(() => route.params.uuid as string);

onBeforeRouteUpdate((to, from, next) => {
  if (to.params.uuid === from.params.uuid) return next();
  return resolvePersonIdentifier(to, from, next);
});

const basicDataProductKey = DATA_UNIT_PRODUCT_KEYS.personBasicData;
const basicDataPriceLabel = computed(() => getDataUnitPriceLabel(basicDataProductKey));
const isCialUser = computed(() => isCialInternalUser(authStore.user?.email));
const hasFeatureAccessOrIsCialUser = computed(() => isCialUser.value || hasFeatureAccess(authStore.user, 'kyt'))
const hasPulseAccess = computed(() => isCialUser.value || hasFeatureAccess(authStore.user, 'pulse'));

const pulseTaxId = computed(() => personStore.cpf ?? personStore.generalData?.taxId ?? null);
const { view: monitoringView } = useEntityMonitoringStatus({
  entityType: 'person',
  taxId: pulseTaxId,
  enabled: hasPulseAccess,
});
const {
  eventCount: pulseEventCount,
  isEventCountLoading: pulseEventCountLoading,
} = useEntityEventCount({
  entityType: 'person',
  taxId: pulseTaxId,
  enabled: hasPulseAccess,
});

// Check whether the account has access to basic data.
// The flag belongs to whichever person the store last loaded, so it only counts once the store
// is synced to the current route. Otherwise it reads as null (unknown) and the page shows the
// skeleton instead of authorizing a render — this is what keeps the child views, which fetch
// basic data on mount, from firing for a person we haven't checked access for yet.
const isStoreSyncedToRoute = computed(() => personStore.currentUuid === uuid.value);
const hasBasicDataAccess = computed(() =>
  isStoreSyncedToRoute.value ? personStore.hasBasicDataAccess : null
);
const isCheckingStatus = computed(() => personStore.basicDataStatusLoading);
const isCpfMissingInReceita = computed(() =>
  personStore.generalData?.taxIdStatus.description === TaxIdStatusDescription.CPF_NOT_FOUND_IN_RECEITA
);
const isPreparingBasicData = computed(() =>
  isCheckingStatus.value || (hasBasicDataAccess.value === true && personStore.loading && !personStore.generalData)
);
const basicDataProviderError = computed(() => personStore.basicDataProviderError);
const shouldShowBanner = computed(() => !isCpfMissingInReceita.value && !basicDataProviderError.value);
const isConfirmRequestDisabled = computed(() => (
  personStore.requestingBasicData || basicDataPriceLoading.value || !basicDataPriceLabel.value
));

// Confirmation modal state
const showConfirmDialog = ref(false);

const openConfirmDialog = () => {
  void loadDataUnitPrices([basicDataProductKey]);
  showConfirmDialog.value = true;
};

const handleCancelRequest = () => {
  showConfirmDialog.value = false;
};

// Handle request access button click
const handleConfirmRequest = async () => {
  showConfirmDialog.value = false;
  const success = await personStore.requestBasicData(uuid.value);
  if (success && personStore.hasBasicDataAccess) {
    // Refresh the general data after successful request
    await personStore.fetchGeneralData(uuid.value);
  }
};

const syncPersonAccessAndData = async (personUuid: string) => {
  await personStore.checkBasicDataStatus(personUuid);

  if (personStore.hasBasicDataAccess) {
    await personStore.fetchGeneralData(personUuid);
  }
};

// Runs immediately during setup — i.e. before child components mount — and resets the store
// synchronously, so nothing can render off the previously viewed person's access flags.
watch(uuid, (newVal) => {
  if (!newVal) return;
  personStore.prepareForUuid(newVal);
  void syncPersonAccessAndData(newVal);
}, { immediate: true });

// Top-level navigation items
interface NavItem {
  name: string;
  routeName: string;
  activePatterns: string[];
  visible?: boolean;
  count?: number | null;
  countLoading?: boolean;
}

const navItems = computed<NavItem[]>(() => [
  { name: 'Dados Gerais', routeName: 'person-general-data', activePatterns: ['person-general'], visible: true },
  { name: 'Relationship Tree', routeName: 'person-relationship-tree', activePatterns: ['person-relationship-tree'], visible: true },
  { name: 'KYX', routeName: 'person-kyc', activePatterns: ['person-kyc'], visible: true },
  { name: 'KYT', routeName: 'person-kyt', activePatterns: ['person-kyt'], visible: hasFeatureAccessOrIsCialUser.value },
  { name: 'Alertas', routeName: 'person-alerts', activePatterns: ['person-alerts'], visible: true },
  { name: 'Investigações', routeName: 'person-investigations', activePatterns: ['person-investigations'], visible: true },
  { name: 'SAR', routeName: 'person-sar', activePatterns: ['person-sar'], visible: true },
  { name: 'Pulse', routeName: 'person-entity-pulse', activePatterns: ['person-entity-pulse'], visible: hasPulseAccess.value, count: pulseEventCount.value, countLoading: pulseEventCountLoading.value },
  { name: 'Outros Dados', routeName: 'person-other-novo-lawsuits', activePatterns: ['person-other'], visible: true },
].filter((item) => item.visible !== false));

const isTabActive = (item: NavItem) => {
  return item.activePatterns.some((pattern: string) => String(route.name).startsWith(pattern));
};

const otherDataSubItems = [
  { name: 'Processos', routeName: 'person-other-novo-lawsuits' },
  { name: 'Certidões', routeName: 'person-other-certificates' },
  { name: 'Mídia IA', routeName: 'person-other-media-ai' },
];

const showOtherDataSubNav = computed(() => String(route.name).startsWith('person-other'));
const isOtherDataSubActive = (routeName: string) => {
  const routePrefix = routeName.endsWith('s') ? routeName.slice(0, -1) : routeName;
  return String(route.name).startsWith(routePrefix);
};
</script>

<template>
  <div class="w-full p-8 bg-gray-50 min-h-full">
    <!-- Banner: always present, auto-switches skeleton/lite/full -->
    <PersonBanner v-if="shouldShowBanner" :monitoring="monitoringView" />

    <div v-if="isCpfMissingInReceita" class="bg-white rounded-2xl shadow-sm border border-gray-200 py-14 px-8 text-center max-w-2xl mx-auto">
      <div class="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
        <BaseIcon size="xl" class="text-gray-500">person_off</BaseIcon>
      </div>
      <h2 class="text-2xl font-semibold text-gray-800 mb-4">CPF não encontrado na Receita Federal</h2>
      <p class="text-gray-600 leading-relaxed">
        O CPF {{ personStore.generalData?.taxId ? formatTaxId(personStore.generalData.taxId) : 'informado' }} não existe na base da Receita Federal.
      </p>
    </div>

    <PersonBasicDataErrorState
      v-else-if="basicDataProviderError"
      :error="basicDataProviderError"
      :tax-id="personStore.cpf"
    />

    <template v-else>

    <!-- Tabs section: show skeleton or real tabs, hide only for no-permission -->
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-to-class="opacity-0"
      mode="out-in"
    >
      <div v-if="hasBasicDataAccess !== false" key="tabs" class="flex gap-6 border-b border-gray-300">
        <template v-if="isPreparingBasicData">
          <!-- Skeleton tabs -->
          <span v-for="i in 7" :key="i" class="inline-block w-20 h-4 my-3 rounded bg-gray-200 animate-pulse"></span>
        </template>
        <template v-else>
          <!-- Real tabs -->
          <router-link
            v-for="item in navItems"
            :key="item.routeName"
            :to="{ name: item.routeName, params: { uuid: uuid } }"
            class="py-3 px-1 no-underline text-gray-600 font-medium text-base border-b-[3px] border-b-transparent transition-colors duration-200 hover:text-blue-900 inline-flex items-center gap-1.5"
            :class="{ '!text-primary-900 !font-semibold !border-b-primary-900': isTabActive(item) }"
          >
            <span>{{ item.name }}</span>
            <span v-if="item.countLoading"
              class="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-surface-300 border-t-primary-600"></span>
            <span v-else-if="item.count"
              class="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary-100 px-1.5 py-0.5 text-xs font-semibold text-primary-700">
              {{ item.count }}
            </span>
          </router-link>
        </template>
      </div>
    </Transition>

    <div v-if="showOtherDataSubNav && !isPreparingBasicData && hasBasicDataAccess === true" class="flex gap-5 mt-2 py-2">
      <router-link
        v-for="subItem in otherDataSubItems"
        :key="subItem.routeName"
        :to="{ name: subItem.routeName, params: { uuid: uuid } }"
        class="px-3 py-1.5 rounded-full text-sm font-medium no-underline transition-colors duration-200 bg-gray-100 text-gray-600 hover:bg-primary-100 hover:text-primary-900"
        :class="{ '!bg-primary-500 !text-primary-50': isOtherDataSubActive(subItem.routeName) }"
      >
        {{ subItem.name }}
      </router-link>
    </div>

    <!-- Content Card: always present -->
    <div class="bg-white p-6 rounded-2xl shadow-sm mt-4 w-full">
      <Transition
        enter-active-class="transition-opacity duration-200 ease-out"
        enter-from-class="opacity-0"
        leave-active-class="transition-opacity duration-150 ease-in"
        leave-to-class="opacity-0"
        mode="out-in"
      >
        <!-- Skeleton content during status check -->
        <div v-if="isPreparingBasicData" key="skeleton" class="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
          <div class="bg-white p-6 rounded-xl border border-gray-200">
            <div class="w-2/5 h-5 mb-6 rounded bg-gray-200 animate-pulse"></div>
            <div class="w-full h-4 mb-3 rounded bg-gray-100 animate-pulse"></div>
            <div class="w-4/5 h-4 mb-3 rounded bg-gray-100 animate-pulse"></div>
            <div class="w-3/5 h-4 rounded bg-gray-100 animate-pulse"></div>
          </div>
          <div class="bg-white p-6 rounded-xl border border-gray-200">
            <div class="w-1/3 h-5 mb-6 rounded bg-gray-200 animate-pulse"></div>
            <div class="w-11/12 h-4 mb-3 rounded bg-gray-100 animate-pulse"></div>
            <div class="w-3/4 h-4 rounded bg-gray-100 animate-pulse"></div>
          </div>
        </div>

        <!-- No permission state -->
        <div v-else-if="hasBasicDataAccess === false" key="no-permission" class="text-center py-12 px-8 max-w-md mx-auto">
          <div class="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-gray-50 to-gray-200 rounded-full flex items-center justify-center">
            <span class="material-icons text-4xl text-gray-500">lock</span>
          </div>
          <h2 class="text-2xl font-semibold text-gray-800 mb-4">Dados não disponíveis</h2>
          <p class="text-gray-500 leading-relaxed mb-6">
            Você ainda não possui acesso aos dados desta pessoa.
            <br />
            Clique no botão abaixo para comprar os dados.
          </p>
          <div class="flex flex-col items-center text-center py-4">
            <p v-if="personStore.cpf" class="text-base text-gray-600 font-medium mb-6 py-2 px-4 bg-gray-50 rounded-lg inline-block">
            CPF: {{ formatTaxId(personStore.cpf) }}
            </p>
            <Button
              label="Comprar dados"
              :loading="personStore.requestingBasicData"
              @click="openConfirmDialog"
              class="font-semibold mt-3"
            >
              <template #icon>
                <BaseIcon>shopping_cart</BaseIcon>
              </template>
            </Button>
          </div>
          
        </div>

        <!-- Normal content when has permission -->
        <div v-else-if="hasBasicDataAccess === true" key="content" class="w-full">
          <!-- Tabs with meta.keepAlive survive tab switches; the cache is dropped with the page on entity change. -->
          <router-view v-slot="{ Component, route: tabRoute }">
            <KeepAlive>
              <component :is="Component" v-if="tabRoute.meta.keepAlive" :key="tabRoute.name" />
            </KeepAlive>
            <component :is="Component" v-if="!tabRoute.meta.keepAlive" />
          </router-view>
        </div>
      </Transition>
    </div>
    </template>
  </div>

  <TokenActionConfirmationDialog
    v-model:visible="showConfirmDialog"
    title="Confirmar compra"
    message-prefix="Você está prestes a comprar os dados básicos desta pessoa."
    message-suffix=""
    :cost-label="basicDataPriceLabel"
    :cost-error="basicDataPriceError"
    :cost-loading="basicDataPriceLoading"
    :confirm-disabled="isConfirmRequestDisabled"
    confirm-label="Comprar"
    @confirm="handleConfirmRequest"
    @cancel="handleCancelRequest"
  />
</template>
